// NotFound.js
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, Search, ArrowLeft, Stethoscope, AlertCircle, HelpCircle } from 'lucide-react';

const NotFound = () => {
  const navigate = useNavigate();

  const quickLinks = [
    { title: 'Find a Doctor', link: '/doctors', icon: Stethoscope },
    { title: 'Emergency Services', link: '/emergency', icon: AlertCircle },
    { title: 'Contact Support', link: '/contact', icon: HelpCircle },
    { title: 'Go to Homepage', link: '/', icon: Home }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center px-4 py-12">
      <div className="max-w-4xl mx-auto text-center">
        {/* 404 Illustration */}
        <div className="mb-8">
          <div className="relative">
            <h1 className="text-9xl md:text-[200px] font-bold text-gray-200">404</h1>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-primary-600 text-white rounded-full p-6">
                <AlertCircle className="w-12 h-12" />
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        <h2 className="text-3xl md:text-4xl font-bold text-secondary-800 mb-4">
          Oops! Page Not Found
        </h2>
        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          The page you're looking for seems to have taken a sick day. 
          Don't worry, we have plenty of healthy pages for you to explore!
        </p>

        {/* Search Bar */}
        <div className="mb-12 max-w-md mx-auto">
          <div className="relative">
            <input
              type="text"
              placeholder="Search for doctors, services, or information..."
              className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.target.value) {
                  navigate(`/search?q=${e.target.value}`);
                }
              }}
            />
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {quickLinks.map((link, index) => {
            const Icon = link.icon;
            return (
              <Link
                key={index}
                to={link.link}
                className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow flex flex-col items-center"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-3">
                  <Icon className="w-6 h-6 text-primary-600" />
                </div>
                <span className="text-secondary-800 font-medium">{link.title}</span>
              </Link>
            );
          })}
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Go Back to Previous Page
        </button>

        {/* Fun Medical Fact */}
        <div className="mt-12 bg-primary-50 rounded-lg p-6 max-w-2xl mx-auto">
          <p className="text-sm text-primary-700">
            <strong>Did you know?</strong> The human body contains approximately 37.2 trillion cells, 
            and they all work together to keep you healthy. Let us help you find the right healthcare 
            services to keep them all happy!
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotFound;